import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { translateInviteTemplate, translateAllInviteTemplates } from "@/lib/admin-referrals.functions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Languages, Plus, Save, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 1;

export const Route = createFileRoute("/admin/pozvanky")({
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Number(search.page) || 1),
  }),
  component: AdminInviteTemplatesPage,
});

type Tpl = {
  id: string;
  key: string;
  subject: string;
  body_cs: string;
  bodies: Record<string, string>;
  active: boolean;
};

function AdminInviteTemplatesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate({ from: "/admin/pozvanky" });
  const { page } = Route.useSearch();
  const translate = useServerFn(translateInviteTemplate);
  const translateAll = useServerFn(translateAllInviteTemplates);

  const [draft, setDraft] = useState({ key: "", subject: "", body_cs: "" });
  const [threshold, setThreshold] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "invite-templates", page],
    queryFn: async () => {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const { data, error, count } = await supabase
        .from("invite_templates")
        .select("id, key, subject, body_cs, bodies, active", { count: "exact" })
        .order("created_at", { ascending: true })
        .range(from, to);
      if (error) throw error;
      return { items: (data ?? []) as Tpl[], total: count ?? 0 };
    },
  });

  const templates = data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  const { data: settings } = useQuery({
    queryKey: ["admin", "referral-threshold"],
    queryFn: async () => {
      const { data } = await supabase
        .from("app_settings").select("value").eq("key", "referral_min_payout_cents").maybeSingle();
      return Number(data?.value ?? 5000);
    },
  });

  const createMut = useMutation({
    mutationFn: async (d: typeof draft) => {
      const { error } = await supabase.from("invite_templates").insert({
        key: d.key.trim(),
        subject: d.subject.trim(),
        body_cs: d.body_cs,
        active: true,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Šablona vytvořena.");
      setDraft({ key: "", subject: "", body_cs: "" });
      qc.invalidateQueries({ queryKey: ["admin", "invite-templates"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveMut = useMutation({
    mutationFn: async (t: Tpl) => {
      const { error } = await supabase.from("invite_templates").update({
        subject: t.subject, body_cs: t.body_cs, bodies: t.bodies, active: t.active,
      }).eq("id", t.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Šablona pozvánky byla úspěšně uložena.");
      qc.invalidateQueries({ queryKey: ["admin", "invite-templates"] });
    },
    onError: (e: Error) =>
      toast.error("Uložení se nezdařilo", {
        description: e.message || "Zkuste to prosím znovu.",
      }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("invite_templates").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Šablona pozvánky byla úspěšně smazána.");
      qc.invalidateQueries({ queryKey: ["admin", "invite-templates"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const translateMut = useMutation({
    mutationFn: async (id: string) => { await translate({ data: { id } }); },
    onSuccess: () => {
      toast.success("Přeloženo do všech jazyků.");
      qc.invalidateQueries({ queryKey: ["admin", "invite-templates"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const translateAllMut = useMutation({
    mutationFn: async (force: boolean) => await translateAll({ data: { force } }),
    onSuccess: (res) => {
      toast.success(
        `Hotovo – zpracováno ${res.templatesProcessed} šablon, doplněno ${res.totalAdded} překladů.`,
      );
      qc.invalidateQueries({ queryKey: ["admin", "invite-templates"] });
      qc.invalidateQueries({ queryKey: ["admin", "invite-templates", "lang-coverage"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Auto-fill missing languages once per session whenever ALL_LANGS gains
  // a new language that some existing template doesn't have yet.
  const autoFilledRef = useRef(false);
  const { data: allTemplatesLangs } = useQuery({
    queryKey: ["admin", "invite-templates", "lang-coverage"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invite_templates")
        .select("id, bodies");
      if (error) throw error;
      return data ?? [];
    },
  });
  useEffect(() => {
    if (autoFilledRef.current) return;
    if (!allTemplatesLangs || allTemplatesLangs.length === 0) return;
    if (translateAllMut.isPending) return;
    const hasMissing = allTemplatesLangs.some((row) => {
      const bodies = (row.bodies ?? {}) as Record<string, string>;
      return ALL_LANGS.some((l) => !(bodies[l] ?? "").trim());
    });
    if (!hasMissing) return;
    autoFilledRef.current = true;
    toast.info("Doplňuji chybějící jazykové verze šablon…");
    translateAllMut.mutate(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTemplatesLangs]);




  const saveThreshold = useMutation({
    mutationFn: async (cents: number) => {
      const { error } = await supabase.from("app_settings")
        .upsert({ key: "referral_min_payout_cents", value: cents as any }, { onConflict: "key" });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Hranice uložena.");
      qc.invalidateQueries({ queryKey: ["admin", "referral-threshold"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl">Šablony zvacích zpráv</h1>
        <p className="text-sm text-muted-foreground">
          Vytvořte univerzální text pozvánky v češtině a nechte ho automaticky přeložit do podporovaných jazyků.
          Použijte placeholder <code>{"{{link}}"}</code> tam, kde se má objevit pozvánkový odkaz.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Hranice pro výplatu provize (WISE)</CardTitle>
          <CardDescription>
            Aktuální nastavení: <strong>{((settings ?? 5000) / 100).toFixed(2)} EUR</strong> (částka v centech: {settings ?? 5000})
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1.5">
              <Label>Nová hranice (v centech)</Label>
              <Input
                type="number"
                min={100}
                placeholder={String(settings ?? 5000)}
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                className="w-40"
              />
            </div>
            <Button
              variant="secondary"
              disabled={!threshold || saveThreshold.isPending}
              onClick={() => saveThreshold.mutate(parseInt(threshold, 10))}
            >
              Uložit
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Nová šablona</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Klíč (např. „default", „friendly")</Label>
              <Input value={draft.key} onChange={(e) => setDraft({ ...draft, key: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Předmět (pro e-mail)</Label>
              <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Text zprávy (česky)</Label>
            <Textarea rows={10} value={draft.body_cs} onChange={(e) => setDraft({ ...draft, body_cs: e.target.value })} />
          </div>
          <Button
            onClick={() => createMut.mutate(draft)}
            disabled={!draft.key.trim() || !draft.body_cs.trim() || createMut.isPending}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" /> Vytvořit
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Hromadné překlady</CardTitle>
          <CardDescription>
            Spustí AI překlad napříč všemi šablonami. „Doplnit chybějící" přidá pouze jazyky, které v šabloně
            zatím chybí (např. bulharštinu). „Přepřeložit vše" přepíše všechny existující překlady novými.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            disabled={translateAllMut.isPending}
            onClick={() => translateAllMut.mutate(false)}
            className="gap-1.5"
          >
            <Languages className="h-4 w-4" />
            {translateAllMut.isPending ? "Překládám…" : "Doplnit chybějící jazyky ve všech šablonách"}
          </Button>
          <Button
            variant="ghost"
            disabled={translateAllMut.isPending}
            onClick={() => translateAllMut.mutate(true)}
            className="gap-1.5"
          >
            <Languages className="h-4 w-4" />
            Přepřeložit vše (přepíše stávající)
          </Button>
        </CardContent>
      </Card>


      {isLoading && <p className="text-sm text-muted-foreground">Načítám…</p>}

      {!isLoading && templates.length === 0 && (
        <p className="text-sm text-muted-foreground">Žádné šablony k zobrazení.</p>
      )}

      {templates.map((tpl) => (
        <TemplateEditor
          key={tpl.id}
          tpl={tpl}
          onSave={(t) => saveMut.mutate(t)}
          onDelete={() => setDeleteId(tpl.id)}
          onTranslate={() => translateMut.mutate(tpl.id)}
          translating={translateMut.isPending}
          saving={saveMut.isPending}
        />
      ))}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Šablona {page} z {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => navigate({ search: { page: page - 1 } })}
            >
              <ChevronLeft className="h-4 w-4" /> Předchozí
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => navigate({ search: { page: page + 1 } })}
            >
              Další <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Smazat šablonu?</AlertDialogTitle>
            <AlertDialogDescription>
              Tato akce je nevratná. Šablona pozvánky bude trvale odstraněna.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)}>Zrušit</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteId) deleteMut.mutate(deleteId);
                setDeleteId(null);
              }}
              disabled={deleteMut.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMut.isPending ? "Mazání…" : "Smazat"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const ALL_LANGS = [
  "pl", "en", "es", "de", "pt", "fr", "hr", "sk", "ro", "it", "hu", "bg",
] as const;

const LANG_NAMES: Record<string, string> = {
  cs: "Čeština", pl: "Polština", en: "Angličtina", es: "Španělština",
  de: "Němčina", pt: "Portugalština", fr: "Francouzština", hr: "Chorvatština",
  sk: "Slovenština", ro: "Rumunština", it: "Italština", hu: "Maďarština",
  bg: "Bulharština",
};

function TemplateEditor({ tpl, onSave, onDelete, onTranslate, translating, saving }: {
  tpl: Tpl;
  onSave: (t: Tpl) => void;
  onDelete: () => void;
  onTranslate: () => void;
  translating: boolean;
  saving: boolean;
}) {
  const [local, setLocal] = useState(tpl);
  const filledCount = ALL_LANGS.filter((l) => (local.bodies?.[l] ?? "").trim().length > 0).length;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{tpl.key}</CardTitle>
          <div className="flex items-center gap-2">
            <Switch checked={local.active} onCheckedChange={(v) => setLocal({ ...local, active: v })} />
            <span className="text-xs text-muted-foreground">aktivní</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label>Předmět</Label>
          <Input value={local.subject} onChange={(e) => setLocal({ ...local, subject: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>{LANG_NAMES.cs} — zdroj (cs)</Label>
          <div className="grid gap-3 md:grid-cols-2">
            <Textarea rows={20} className="min-h-[420px] font-mono text-sm" value={local.body_cs} onChange={(e) => setLocal({ ...local, body_cs: e.target.value })} />
            <InvitePreview label="cs" body={local.body_cs} />
          </div>
        </div>

        <div className="space-y-3 rounded-md border p-3">
          <Label className="text-sm font-semibold">
            Překlady ({filledCount} / {ALL_LANGS.length})
          </Label>
          <div className="space-y-4">
            {ALL_LANGS.map((l) => {
              const value = local.bodies?.[l] ?? "";
              const empty = value.trim().length === 0;
              return (
                <div key={l} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="uppercase">{l}</Badge>
                      <span className="text-sm font-medium">{LANG_NAMES[l] ?? l}</span>
                      {empty && (
                        <Badge variant="outline" className="text-xs">chybí překlad</Badge>
                      )}
                    </div>
                    {!empty && (
                      <button
                        type="button"
                        className="text-xs text-destructive hover:underline"
                        onClick={() => {
                          const next = { ...(local.bodies ?? {}) };
                          delete next[l];
                          setLocal({ ...local, bodies: next });
                        }}
                      >
                        Odstranit překlad
                      </button>
                    )}
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <Textarea
                      rows={20}
                      className="min-h-[420px] font-mono text-sm"
                      placeholder={empty ? `Zadejte překlad do jazyka: ${LANG_NAMES[l] ?? l}` : undefined}
                      value={value}
                      onChange={(e) =>
                        setLocal({ ...local, bodies: { ...(local.bodies ?? {}), [l]: e.target.value } })
                      }
                    />
                    <InvitePreview label={l} body={value} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => onSave(local)} disabled={saving} className="gap-1.5">
            <Save className="h-4 w-4" /> {saving ? "Ukládám…" : "Uložit"}
          </Button>
          <Button size="sm" variant="secondary" onClick={onTranslate} disabled={translating} className="gap-1.5">
            <Languages className="h-4 w-4" /> {translating ? "Překládám…" : "Přeložit do všech jazyků"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} className="gap-1.5 text-destructive">
            <Trash2 className="h-4 w-4" /> Smazat
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


const SAMPLE_LINK = "https://cerveza.international/pozvanka/UKAZKA123";

function renderPreview(body: string): string {
  return (body ?? "").replaceAll("{{link}}", SAMPLE_LINK);
}

function InvitePreview({ label, body }: { label: string; body: string }) {
  const text = renderPreview(body);
  const parts = text.split(new RegExp(`(${SAMPLE_LINK.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`));
  return (
    <div className="flex flex-col rounded-md border bg-muted/30 p-3 text-sm">
      <div className="mb-1.5 flex items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="outline" className="uppercase">{label}</Badge>
        <span>Živý náhled</span>
      </div>
      <div className="whitespace-pre-wrap break-words leading-relaxed">
        {body?.trim()
          ? parts.map((p, i) =>
              p === SAMPLE_LINK
                ? <a key={i} href={SAMPLE_LINK} className="text-primary underline" onClick={(e) => e.preventDefault()}>{SAMPLE_LINK}</a>
                : <span key={i}>{p}</span>
            )
          : <span className="text-muted-foreground italic">(prázdné)</span>}
      </div>
    </div>
  );
}

